import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { usePermissions } from "../../PermissionsProvider";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tooltip } from "../ui/tooltip";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import CompletionDetailsPanel from "./CompletionDetailsPanel";
import { Card } from "../ui/card";
import {
  User,
  Bot,
  Send,
  Mic,
  Clock,
  GitBranch,
  Scissors,
  X,
  Pause,
  HelpCircle,
  FileText,
  Code,
  ImagePlus,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { renderMarkdown } from "../../utils/markdownUtils";
import { formatMessageContent } from "../../utils/messageUtils";
import ModelInput from "./ModelInput";
import { SandboxMessage, SandboxSession } from "../../model_gen/sandbox";
import { Agent } from "../../model_gen/agent_base";
import { SandboxService } from "../../services/SandboxService";
import { ConfigService } from "../../services/ConfigService";
import { EvaluateResponse, EvaluateResult } from "../../model_gen/evaluation";
import { Sentinel } from "../../model_gen/sentinel";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import MetaLogo from "../../assets/meta_primary.svg";
import ReconnectingWebSocket from "reconnecting-websocket";
import { GeneralService } from "../../services/GeneralService";

interface AgentSettings {
  max_iterations?: number | null;
  execution_mode?: string | null;
  high_performance?: boolean | null;
  disable_chaining?: boolean | null;
  model?: string | null;
  version?: {
    agent_version_number?: string | null;
    agent_release_name?: string | null;
  } | null;
}

export interface ChatProps {
  onHasMessagesChange: (hasMessages: boolean) => void;
  resetTrigger: number;
  models?: string[];
  temperature?: number;
  evaluations?: boolean;
  autocorrect?: boolean;
  retrieveContext?: boolean;
  streaming?: boolean;
  systemPrompt?: string;
  assistantFirst?: boolean;
  conversationSummaryMode?: boolean;
  context?: string;
  applicationName?: string;
  onConversationChange?: (conversation: any) => void;
}

export interface ChatRef {
  sendFaultyMessage: (sentinel: Sentinel) => void;
  clearConversation: () => void;
}

interface Conversation {
  turns: Turn[];
}

interface Turn {
  sender: "USER" | "BOT";
  messages: SandboxMessage[];
}

interface ModelTiming {
  model: string;
  startTime: number;
  ttftTime: number | null;
  ttftValue: number | null;
  completionTime: number | null;
  inferenceTime: number | null;
}

interface TurnModelTimings {
  turnIndex: number;
  modelTimings: ModelTiming[];
}

interface InterruptionPoint {
  messageId: string | null;
  position: number | null;
}

interface TranscriptionResponse {
  text: string;
  status: "success" | "error";
  error?: string;
  message?: string;
  transcript?: string;
  is_final?: boolean;
  processing_time?: number;
  cleaning_time?: number;
  transcription_time?: number;
}

// Audio Visualization Component
const AudioVisualization: React.FC<{
  levels: number[];
  isRecording: boolean;
}> = ({ levels, isRecording }) => {
  if (!isRecording) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        pointerEvents: "none",
      }}>
      {levels.map((level, index) => (
        <div
          key={index}
          style={{
            width: "2px",
            height: `${Math.max(2, level * 16)}px`,
            backgroundColor: "#ff4d4f",
            borderRadius: "1px",
            transition: "height 0.1s ease",
            opacity: 0.8 + level * 0.2,
          }}
        />
      ))}
    </div>
  );
};

const Chat = forwardRef<ChatRef, ChatProps>(
  (
    {
      onHasMessagesChange,
      resetTrigger,
      models = [],
      temperature = 0.7,
      evaluations = false,
      autocorrect = false,
      retrieveContext = false,
      streaming = true,
      systemPrompt,
      assistantFirst = false,
      conversationSummaryMode = false,
      context,
      applicationName,
      onConversationChange,
    },
    ref
  ) => {
    const { apiKey, companyId } = usePermissions();
    const [conversation, setConversation] = useState<Conversation>({
      turns: [],
    });
    const conversationRef = useRef<Conversation>({ turns: [] });
    const [inputValue, setInputValue] = useState("");
    const [attachedImages, setAttachedImages] = useState<string[]>([]);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [isTyping, setIsTyping] = useState(false);
    const [selectedModel, setSelectedModel] = useState<string>("");
    const selectedModelRef = useRef<string>("");
    const [agentId, setAgentId] = useState<number | null>(null);
    const [agentSettings, setAgentSettings] = useState<AgentSettings | null>(
      null
    );

    // Debug selectedModel changes
    useEffect(() => {
      console.log("selectedModel state changed to:", selectedModel);
    }, [selectedModel]);
    const [session, setSession] = useState<SandboxSession | undefined>(
      undefined
    );
    const [agents, setAgents] = useState<Agent[]>([]);
    const [modelTimings, setModelTimings] = useState<ModelTiming[]>([]);
    const [allTurnTimings, setAllTurnTimings] = useState<TurnModelTimings[]>(
      []
    );
    const modelTimingsRef = useRef<ModelTiming[]>([]);
    const [animationFrame, setAnimationFrame] = useState<number | null>(null);
    const [currentTime, setCurrentTime] = useState<number>(Date.now());
    const [restoreCheckpointOnNextMessage, setRestoreCheckpointOnNextMessage] =
      useState(false);
    const processedTokenRefs = useRef<Record<string, boolean>>({});
    const [markdownEnabledMessages, setMarkdownEnabledMessages] = useState<
      Set<string>
    >(new Set());

    // New state variables for advanced features
    const [evalResponses, setEvalResponses] = useState<EvaluateResponse[]>([]);
    const [strobeMessage, setStrobeMessage] = useState<{
      id: string | null;
      color: string;
    }>({ id: null, color: "" });
    const [interruptionPoints, setInterruptionPoints] = useState<
      InterruptionPoint[]
    >([]);
    const [interruptionMode, setInterruptionMode] = useState<boolean>(false);
    const [interruptionHelperText, setInterruptionHelperText] =
      useState<string>("");
    const [selectedMessageForInterruption, setSelectedMessageForInterruption] =
      useState<SandboxMessage | null>(null);
    const interruptionModeRef = useRef<boolean>(false);

    // Voice recording states
    const [isRecording, setIsRecording] = useState(false);
    const [isConnecting, setIsConnecting] = useState(true);
    const wsRef = useRef<ReconnectingWebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const onMessageHandlerRef = useRef((_event: MessageEvent) => {});
    const pausedRecordingRef = useRef(false);
    const [audioLevels, setAudioLevels] = useState<number[]>([0, 0, 0, 0, 0]);
    const audioLevelAnimationRef = useRef<number | null>(null);

    // Tour state
    const [tourOpen, setTourOpen] = useState(false);
    const [tourStep, setTourStep] = useState(0);
    const [showTourButton, setShowTourButton] = useState(true);

    // Metrics panel state
    const [metricsOpen, setMetricsOpen] = useState(false);
    const [metricsMessage, setMetricsMessage] = useState<SandboxMessage | null>(
      null
    );

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Model color mapping for consistency
    const modelColorMap: Record<string, string> = {
      "gpt-3.5-turbo": "#10a37f",
      "gpt-4": "#8e44ad",
      "gpt-4-turbo": "#9b59b6",
      "gpt-4o": "#ab68d9",
      "gpt-4o-mini": "#ffa658",
      "claude-3-opus": "#e67e22",
      "claude-3-sonnet": "#d35400",
      "claude-3-haiku": "#e74c3c",
      "claude-3.5-sonnet": "#ff6b35",
      "mistral-medium": "#3498db",
      "mistral-small": "#2980b9",
      "llama-3.1-8b": "#21b892",
      "llama-3.1-70b": "#1a9876",
      red_robin: "#21b892",
    };

    // Model label mapping for display
    const modelLabelMap: Record<string, React.ReactNode> = {
      "maitai:red_robin": (
        <span className="flex items-center justify-center gap-1">
          maitai-groq-lora
          <img src={MetaLogo} alt="Meta" className="h-5" />
        </span>
      ),
      "maitai:predibase-llama-3.1-8b-instruct": (
        <span className="flex items-center justify-center gap-1">
          llama-3.1-8b
          <img src={MetaLogo} alt="Meta" className="h-5" />
        </span>
      ),
      "openai:gpt-4o-mini": "gpt-4o-mini",
      "openai:gpt-4o": "gpt-4o",
    };

    // Get color for a model (with fallback)
    const getModelColor = (modelName: string): string => {
      const baseModelName = modelName?.toLowerCase().split(":").pop() || "";
      if (baseModelName && modelColorMap[baseModelName]) {
        return modelColorMap[baseModelName];
      }
      const matchingModel = Object.keys(modelColorMap).find((model) =>
        baseModelName.includes(model)
      );
      return matchingModel ? modelColorMap[matchingModel] : "#666666";
    };

    // Get user-friendly label for a model
    const getModelLabel = (modelName: string): React.ReactNode => {
      if (modelName && modelLabelMap[modelName]) {
        return modelLabelMap[modelName];
      }

      if (modelName && modelName.startsWith("agent:")) {
        const agentIdStr = modelName.split(":")[1];
        const agent = agents.find((a) => a.id?.toString() === agentIdStr);
        if (agent && agent.agent_name) {
          return `agent:${agent.agent_name}`;
        }
        return modelName;
      }

      return modelName?.split(":").pop() || modelName || "";
    };

    // Function to determine timing color
    const getTimingColor = (time: number, isTTFT: boolean = false): string => {
      if (isTTFT) {
        if (time < 300) return "#52c41a";
        if (time < 400) return "#faad14";
        return "#f5222d";
      } else {
        if (time <= 500) return "#52c41a";
        if (time <= 650) return "#faad14";
        return "#f5222d";
      }
    };

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Function to update current time for animation
    const updateCurrentTime = useCallback(() => {
      setCurrentTime(Date.now());
      const frame = requestAnimationFrame(updateCurrentTime);
      setAnimationFrame(frame);
    }, []);

    // Start/stop animation when model timings change
    useEffect(() => {
      modelTimingsRef.current = modelTimings;

      const hasActiveTimings =
        modelTimings.length > 0 &&
        modelTimings.some((t) => t.completionTime === null);

      if (hasActiveTimings && !animationFrame) {
        const frame = requestAnimationFrame(updateCurrentTime);
        setAnimationFrame(frame);
      } else if (!hasActiveTimings && animationFrame) {
        cancelAnimationFrame(animationFrame);
        setAnimationFrame(null);
      }

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }, [modelTimings, animationFrame, updateCurrentTime]);

    // Effect to store turn timings when a turn is complete
    useEffect(() => {
      if (
        modelTimings.length > 0 &&
        modelTimings.some(
          (timing) =>
            timing.ttftValue !== null ||
            timing.ttftTime !== null ||
            timing.inferenceTime !== null
        )
      ) {
        const currentTurnIndex = conversation.turns.length - 1;
      
      // Use functional update to access previous state without needing it in dependencies
      setAllTurnTimings((prev) => {
          const existingTurnIndex = prev.findIndex(
          (t) => t.turnIndex === currentTurnIndex
        );

        if (existingTurnIndex >= 0) {
              const newTimings = [...prev];
            newTimings[existingTurnIndex] = {
              turnIndex: currentTurnIndex,
              modelTimings: [...modelTimings],
            };
            return newTimings;
          } else {
            return [
            ...prev,
            {
              turnIndex: currentTurnIndex,
              modelTimings: [...modelTimings],
            },
            ];
        }
      });
      }
    }, [modelTimings, conversation.turns.length]);

    useEffect(() => {
      conversationRef.current = conversation;
      scrollToBottom();
    }, [conversation]);

    useEffect(() => {
      scrollToBottom();
    }, [modelTimings]);

    // Notify parent when messages state changes
    useEffect(() => {
      const hasMessages = conversation.turns.some((turn) =>
        turn.messages.some((msg) => msg.message && msg.message.trim() !== "")
      );
      onHasMessagesChange(hasMessages);
    }, [conversation, onHasMessagesChange]);

    // Fetch agents to display agent names
    useEffect(() => {
      if (!apiKey) return;

      console.log(
        "Fetching agents with API key:",
        typeof apiKey === "string" ? apiKey.length : "auth context"
      );

      const agentsSubscription = ConfigService.getAgents(
        null,
        apiKey
      ).subscribe(
        (response: any) => {
          const fetchedAgents = Array.isArray(response)
            ? response
            : response.agents || [];
          setAgents(fetchedAgents);
        },
        (error: any) => {
          console.error("Error fetching agents:", error);
        }
      );

      return () => {
        agentsSubscription.unsubscribe();
      };
    }, [apiKey]);

    // Reset conversation when resetTrigger changes
    useEffect(() => {
      if (resetTrigger > 0) {
        clearConversation();
      }
    }, [resetTrigger]);

    const clearConversation = () => {
      setConversation({ turns: [] });
      conversationRef.current = { turns: [] };
      processedTokenRefs.current = {};
      setModelTimings([]);
      setAllTurnTimings([]);
      setSession(undefined);
      setInputValue("");
      setIsTyping(false);
      setSelectedModel("");
      setAgentId(null);
      setAgentSettings(null);
      setEvalResponses([]);
      setInterruptionPoints([]);
      setInterruptionMode(false);
      setSelectedMessageForInterruption(null);
    };

    // Imperative handle for external control
    useImperativeHandle(
      ref,
      () => ({
        sendFaultyMessage: (sentinel: Sentinel) => {
          const msg = `<${sentinel.sentinel_name}>`;
          handleSend(msg);
        },
        clearConversation: () => {
          clearConversation();
        },
      }),
      []
    );

    // Add helper function for number spacing (Red Robin specific)
    const addNumberSpacing = (text: string | null | undefined): string => {
      if (!text) return "";
      text = text.replace(/([a-zA-Z])(\d)/g, "$1 $2");
      text = text.replace(/(\d)([a-zA-Z])/g, "$1 $2");
      return text;
    };

    // Helper function to handle token updates consistently
    const handleTokenUpdate = useCallback(
      (
        tokenUpdate: SandboxMessage,
        sessionObj: SandboxSession | undefined
      ): void => {
        if (!tokenUpdate.message || tokenUpdate.message.trim() === "") {
          return;
        }

        const tokenId = `${tokenUpdate.meta?.reference_id}_${
          tokenUpdate.meta?.token_id || uuidv4()
        }`;

        if (processedTokenRefs.current[tokenId]) {
          return;
        }

        processedTokenRefs.current[tokenId] = true;

        const modelName = (tokenUpdate.meta?.model as string) || "";
        const messageKey = `${tokenUpdate.meta?.reference_id}_${
          tokenUpdate.message?.length || 0
        }`;

        setConversation((prevConversation) => {
          const turns = [...prevConversation.turns];
          const lastTurn = turns[turns.length - 1];
          const newTokenMessage = modelName.toLowerCase().includes("red_robin")
            ? addNumberSpacing(tokenUpdate.message)
            : tokenUpdate.message;

          if (lastTurn && lastTurn.sender === "BOT") {
            const existingMessageIndex = lastTurn.messages.findIndex(
              (msg) => msg.meta?.reference_id === tokenUpdate.meta?.reference_id
            );

            if (existingMessageIndex >= 0) {
              const updatedMessages = [...lastTurn.messages];
              const existingMessage = updatedMessages[existingMessageIndex];
              const newMessage =
                (existingMessage.message || "") + newTokenMessage;
              updatedMessages[existingMessageIndex] = {
                ...existingMessage,
                message: modelName.toLowerCase().includes("red_robin")
                  ? addNumberSpacing(newMessage)
                  : newMessage,
                meta: {
                  ...existingMessage.meta,
                  ...tokenUpdate.meta,
                  streaming: true,
                  renderKey: messageKey,
                  session: sessionObj,
                },
              };
              turns[turns.length - 1] = {
                ...lastTurn,
                messages: updatedMessages,
              };
            } else {
              turns[turns.length - 1] = {
                ...lastTurn,
                messages: [
                  ...lastTurn.messages,
                  {
                    ...tokenUpdate,
                    message: newTokenMessage,
                    meta: {
                      ...tokenUpdate.meta,
                      streaming: true,
                      renderKey: messageKey,
                      session: sessionObj,
                    },
                  },
                ],
              };
            }
          } else {
            turns.push({
              sender: "BOT",
              messages: [
                {
                  ...tokenUpdate,
                  message: newTokenMessage,
                  meta: {
                    ...tokenUpdate.meta,
                    streaming: true,
                    renderKey: messageKey,
                    session: sessionObj,
                  },
                },
              ],
            });
          }

          return { ...prevConversation, turns };
        });
      },
      []
    );

    // Keep input focused after sends and when streaming completes
    useEffect(() => {
      if (!isTyping) {
        // Slight delay to avoid focus being stolen by other renders
        setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
      }
    }, [isTyping]);

    // Helper function to add a new turn to the conversation
    const addTurn = useCallback(
      (sender: "USER" | "BOT", messages: SandboxMessage[]) => {
        setConversation((prevConversation) => ({
          turns: [...prevConversation.turns, { sender, messages }],
        }));

        // Enable markdown by default for bot messages
        if (sender === "BOT") {
          setMarkdownEnabledMessages((prev) => {
            const newSet = new Set(prev);
            messages.forEach((msg) => {
              if (msg.id) {
                newSet.add(String(msg.id));
              }
            });
            return newSet;
          });
        }
      },
      []
    );

    // Helper function to update a message in the conversation
    const updateMessageInConversation = useCallback(
      (
        messageId: number | string,
        updateFn: (message: SandboxMessage) => SandboxMessage
      ) => {
        setConversation((prevConversation) => {
          const newTurns = prevConversation.turns.map((turn) => {
            const updatedMessages = turn.messages.map((msg) =>
              msg.id === messageId ? updateFn(msg) : msg
            );
            return { ...turn, messages: updatedMessages };
          });
          return { ...prevConversation, turns: newTurns };
        });
      },
      []
    );

    const handleSend = (override: string | null = null) => {
      const tmp = override || inputValue;
      if (!tmp.trim()) return;

      if (!apiKey) {
        toast.error(
          "Authentication token not available. Please refresh the page."
        );
        return;
      }

      if (!session) {
        // First message - need to create session first, then send the message
        handleStartConversation(tmp);
        return;
      }

      // Subsequent messages - session already exists
      sendMessage(tmp);
    };

    const handleSendClick = () => {
      handleSend();
    };

    // Image handling functions
    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      Array.from(files).forEach((file) => {
        if (!file.type.startsWith("image/")) {
          toast.error("Please select an image file");
          return;
        }

        // Max 20MB per image
        if (file.size > 20 * 1024 * 1024) {
          toast.error("Image must be less than 20MB");
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          setAttachedImages((prev) => [...prev, dataUrl]);
        };
        reader.readAsDataURL(file);
      });

      // Reset file input
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    };

    const removeAttachedImage = (index: number) => {
      setAttachedImages((prev) => prev.filter((_, i) => i !== index));
    };

    // Drag and drop handlers for images
    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      
      // Only allow drop for models, not agents
      if (agentId || !selectedModel) return;
      
      const files = event.dataTransfer.files;
      if (!files || files.length === 0) return;

      Array.from(files).forEach((file) => {
        if (!file.type.startsWith("image/")) {
          toast.error("Please drop an image file");
          return;
        }

        // Max 20MB per image
        if (file.size > 20 * 1024 * 1024) {
          toast.error("Image must be less than 20MB");
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          setAttachedImages((prev) => [...prev, dataUrl]);
        };
        reader.readAsDataURL(file);
      });
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleStartConversation = (messageOverride?: string) => {
      const currentModel = selectedModelRef.current;
      const messageToSend = messageOverride || inputValue;

      if (!currentModel || !messageToSend.trim()) {
        return;
      }

      setInputValue("");
      setIsTyping(true);

      console.log("Creating new session with model:", currentModel);

      const sessionData: SandboxSession = {
        sandbox_application_id: null,
        meta: {
          model: currentModel,
          temperature: 0.7,
        },
      };

      const subscription = SandboxService.newSessionStream(
        sessionData,
        apiKey,
        undefined
      ).subscribe(
        (result: any) => {
          console.log("Session stream result:", result);
          if (result.type === "session") {
            const newSession = result.data;
            setSession(newSession);
            // Session created, now send the first message
            sendMessage(messageToSend, newSession);
          }
          if (result.type === "bot_message") {
            addTurn("BOT", [result.data]);
            setIsTyping(false);
          }
        },
        (error: any) => {
          console.error("Error creating session", error);
          toast.error(
            "Failed to create session. Please check your authentication."
          );
          setIsTyping(false);
          // Restore the input value if session creation failed
          setInputValue(messageToSend);
        }
      );

      return () => subscription.unsubscribe();
    };

    const sendMessage = (
      messageText: string,
      sessionOverride?: SandboxSession
    ) => {
      const activeSession = sessionOverride || session;
      if (!activeSession) {
        console.error("No active session");
        return;
      }

      // Reset model timings for new message
      setModelTimings([]);

      // Capture current attached images before clearing - only for models, not agents
      const imagesToSend = !agentId ? [...attachedImages] : [];
      const messageContent = imagesToSend.length > 0 ? formatMessageContent(messageText, imagesToSend) : undefined;

      const tempId = Date.now();
      const tempNewMessage: SandboxMessage = {
        id: tempId,
        message: messageText,
        sender: "USER",
        sandbox_session_id: activeSession?.id,
        meta: {
          session: activeSession,
          attached_images: imagesToSend.length > 0 ? imagesToSend : undefined,
          message_content: messageContent,
        },
      };

      addTurn("USER", [tempNewMessage]);

      // Clear the input ONLY if we're sending from the current input
      // (not when called from handleStartConversation where it's already cleared)
      if (!sessionOverride) {
        setInputValue("");
      }
      // Clear attached images
      setAttachedImages([]);

      setIsTyping(true);

      // Send message for the selected model - use the model from the message meta if available
      const sessionModel = activeSession?.meta?.model;
      const modelToUse =
        (typeof sessionModel === "string" ? sessionModel : null) ||
        selectedModelRef.current;
      console.log(
        "Using model:",
        modelToUse,
        "from session meta:",
        sessionModel,
        "or selectedModelRef:",
        selectedModelRef.current
      );
      handleSendForModel(modelToUse, messageText, tempId, activeSession, messageContent);
    };

    const handleSendForModel = (
      model: string,
      messageText: string,
      tempId: number,
      activeSession: SandboxSession,
      messageContent?: string | object[]
    ) => {
      const conversationMessages = conversationRef.current.turns
        .flatMap((turn) => turn.messages)
        .map((msg) => ({
          id: msg.id,
          message: msg.message,
          sender: msg.sender,
          sandbox_session_id: msg.sandbox_session_id,
          meta: msg.meta,
        }));

      const messageToSend: Omit<SandboxMessage, "id"> = {
        message: messageText,
        sender: "USER",
        sandbox_session_id: activeSession?.id,
        sandbox_application_id: activeSession?.sandbox_application_id,
        session_token: activeSession?.token,
        company_id: companyId ?? -1,
        meta: {
          model: model,
          temperature: temperature,
          evaluations: evaluations,
          autocorrect: autocorrect,
          retrieve_context: retrieveContext,
          streaming: streaming,
          client_start_time: Date.now(),
          session: activeSession,
          agent_id: agentId ?? null,
          agent_settings: agentSettings ?? null,
          restore_checkpoint: restoreCheckpointOnNextMessage,
          // Include multimodal content if images are attached
          message_content: messageContent,
        },
      };

      console.log("🚀 [Chat] Sending message with:");
      console.log("  - model:", model);
      console.log("  - agentId:", agentId);
      console.log("  - agentSettings:", JSON.stringify(agentSettings, null, 2));
      console.log("  - ✅ Version info present:", agentSettings?.version);


      if (restoreCheckpointOnNextMessage) {
        setRestoreCheckpointOnNextMessage(false);
      }

      // Add model timing entry
      setModelTimings((prev) => [
        ...prev,
        {
          model,
          startTime: Date.now(),
          ttftTime: null,
          ttftValue: null,
          completionTime: null,
          inferenceTime: null,
        },
      ]);

      const subscription = SandboxService.newMessageStream(
        messageToSend as SandboxMessage,
        conversationMessages,
        apiKey
      ).subscribe(
        (response: any) => {
          let client_first_token_time = -1;
          if (response.type === "user_message") {
            updateMessageInConversation(tempId, (existingMsg) => ({
              ...response.data,
              meta: {
                ...response.data.meta,
                // Preserve attached_images from the original message
                attached_images: existingMsg.meta?.attached_images,
              },
            }));
          } else if (response.type === "token_update") {
            if (client_first_token_time === -1) {
              client_first_token_time = Date.now();
              const modelName = (response.data?.meta?.model as string) || "";

              setModelTimings((prev) =>
                prev.map((timing) =>
                  timing.model === modelName && timing.ttftTime === null
                    ? {
                        ...timing,
                        ttftTime: client_first_token_time,
                      }
                    : timing
                )
              );
            }
            handleTokenUpdate(response.data, activeSession);
            // Keep typing state active while streaming tokens
            setIsTyping(true);
          } else if (response.type === "bot_message") {
            const botMessage = response.data;
            botMessage.meta["client_end_time"] = Date.now();
            if (botMessage.meta?.ttft_diff) {
              botMessage.meta["client_end_time"] =
                botMessage.meta["client_end_time"] -
                botMessage.meta["ttft_diff"];
            }

            const modelName = (botMessage.meta?.model as string) || "";
            const infTime = botMessage.meta?.inf_time as number;
            const ttftFromMeta = botMessage.meta?.ttft as number;

            setModelTimings((prev) =>
              prev.map((timing) =>
                timing.model === modelName && timing.completionTime === null
                  ? {
                      ...timing,
                      completionTime: Date.now(),
                      inferenceTime: infTime || null,
                      ttftValue: ttftFromMeta || null,
                    }
                  : timing
              )
            );

            // Enable markdown for this bot message by default
            if (botMessage.id) {
              setMarkdownEnabledMessages((prev) => {
                const newSet = new Set(prev);
                newSet.add(String(botMessage.id));
                return newSet;
              });
            }

            setConversation((prevConversation) => {
              const turns = [...prevConversation.turns];
              const lastTurn = turns[turns.length - 1];

              if (lastTurn && lastTurn.sender === "BOT") {
                const existingMessageIndex = lastTurn.messages.findIndex(
                  (msg) =>
                    msg.meta?.reference_id === botMessage.meta?.reference_id
                );

                if (existingMessageIndex >= 0) {
                  const updatedMessages = [...lastTurn.messages];
                  const existingMessage = updatedMessages[existingMessageIndex];
                  updatedMessages[existingMessageIndex] = {
                    ...existingMessage,
                    ...botMessage,
                    message:
                      existingMessage.message || botMessage.message || "",
                    meta: {
                      ...existingMessage.meta,
                      ...botMessage.meta,
                      streaming: false,
                      session: activeSession,
                    },
                  };
                  turns[turns.length - 1] = {
                    ...lastTurn,
                    messages: updatedMessages,
                  };
                } else {
                  turns[turns.length - 1] = {
                    ...lastTurn,
                    messages: [
                      ...lastTurn.messages,
                      {
                        ...botMessage,
                        meta: {
                          ...botMessage.meta,
                          streaming: false,
                          session: activeSession,
                        },
                      },
                    ],
                  };
                }
              } else {
                turns.push({
                  sender: "BOT",
                  messages: [
                    {
                      ...botMessage,
                      meta: {
                        ...botMessage.meta,
                        streaming: false,
                        session: activeSession,
                      },
                    },
                  ],
                });
              }

              return { ...prevConversation, turns };
            });

            setIsTyping(false);
          }
        },
        (error: any) => {
          console.error("Error in message stream:", error);
          toast.error("Failed to send message");
          setIsTyping(false);
        },
        () => {
          console.log("Message stream completed");
          setIsTyping(false);
        }
      );

      return subscription;
    };

    const handleBranchFromTurn = (turnIndex: number, msg: SandboxMessage) => {
      if (!session) {
        toast.error("Cannot branch: session not available");
        return;
      }

      let turnNumber = 0;
      for (let i = 0; i <= turnIndex; i++) {
        const turn = conversation.turns[i];
        if (turn && turn.sender === "BOT") {
          turnNumber++;
        }
      }

      const messageSession = msg.meta?.session as SandboxSession | undefined;
      const sessionIdForBranch =
        messageSession?.token ||
        messageSession?.id ||
        session.token ||
        session.id;

      const subscription = SandboxService.branchSession(
        -1,
        agentId || -1, // Use -1 for regular LLM models
        String(sessionIdForBranch),
        turnNumber - 1,
        session.sandbox_application_id || null,
        apiKey
      ).subscribe(
        (result: any) => {
          if (result.session) {
            clearConversation();
            setSession(result.session);
            setRestoreCheckpointOnNextMessage(true);

            const branchedConversation: Conversation = {
              turns: conversation.turns.slice(0, turnIndex + 1),
            };
            setConversation(branchedConversation);
            conversationRef.current = branchedConversation;

            toast.success(
              `Branched from turn ${
                turnIndex + 1
              }. Continue from here with a new path.`
            );
          }
        },
        (error: any) => {
          console.error("Error branching session:", error);
          toast.error("Failed to branch conversation. Please try again.");
        }
      );

      return () => subscription.unsubscribe();
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    // Voice recording functions
    const initializeWebSocket = () => {
      if (wsRef.current) {
        return;
      }

      wsRef.current = new ReconnectingWebSocket(
        `${GeneralService.PROD_ENDPOINT}/breeze/ws`,
        [],
        {
          maxRetries: 10,
          reconnectionDelayGrowFactor: 1.3,
          maxReconnectionDelay: 10000,
          minReconnectionDelay: 1000,
        }
      );

      wsRef.current.onopen = () => {
        setIsConnecting(false);
        toast.success("Connected to transcription server");
      };

      wsRef.current.onclose = (event) => {
        setIsConnecting(true);
        toast.error("Disconnected from transcription server");
      };

      wsRef.current.onerror = (error) => {
        toast.error("Transcription WebSocket error occurred");
      };

      wsRef.current.onmessage = (event) => {
        onMessageHandlerRef.current(event);
      };
    };

    const startRecording = async () => {
      pausedRecordingRef.current = false;
      try {
        initializeWebSocket();

        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          toast.info("Connecting to transcription server...");
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Connection timeout"));
            }, 10000);

            const checkConnection = () => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                clearTimeout(timeout);
                resolve();
              } else {
                setTimeout(checkConnection, 100);
              }
            };
            checkConnection();
          });
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: 16000,
          },
        });
        mediaStreamRef.current = stream;

        const audioContext = new AudioContext({
          sampleRate: 16000,
          latencyHint: "interactive",
        });
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(1024, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            const inputData = e.inputBuffer.getChannelData(0);

            let sum = 0;
            for (let i = 0; i < inputData.length; i++) {
              sum += inputData[i] * inputData[i];
            }
            const rms = Math.sqrt(sum / inputData.length);
            const level = Math.min(1, rms * 10);

            setAudioLevels((prevLevels) => {
              const newLevels = [...prevLevels.slice(1), level];
              return newLevels;
            });

            const pcmData = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              pcmData[i] = Math.max(
                -32768,
                Math.min(32767, Math.round(inputData[i] * 32767))
              );
            }

            const base64Audio = btoa(
              String.fromCharCode.apply(
                null,
                Array.from(new Uint8Array(pcmData.buffer))
              )
            );

            const message = {
              type: "audio",
              audio: base64Audio,
              format: "raw",
              sampleRate: 16000,
              channels: 1,
            };
            wsRef.current.send(JSON.stringify(message));
          }
        };

        source.connect(processor);
        processor.connect(audioContext.destination);

        const startMessage = {
          type: "start",
          context: context?.trim() || undefined,
          full_conversation: "", // Could be enhanced to include conversation history
        };

        wsRef.current?.send(JSON.stringify(startMessage));

        setIsRecording(true);
        setInputValue("");
        toast.success("Started recording");
      } catch (err) {
        if (err instanceof Error && err.message === "Connection timeout") {
          toast.error(
            "Failed to connect to transcription server. Please check if the server is running."
          );
        } else {
          toast.error("Failed to access microphone. Please check permissions.");
        }
      }
    };

    const stopRecording = () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      setIsRecording(false);
      setAudioLevels([0, 0, 0, 0, 0]);

      wsRef.current?.send(JSON.stringify({ type: "stop" }));
      toast.success("Stopped recording");
    };

    // Interruption handling functions
    const handleMessageDoubleClick = (
      message: SandboxMessage,
      event: React.MouseEvent
    ) => {
      // Allow interruption points on any bot message
      if (message.sender === "BOT") {
        setInterruptionMode(true);
        setInterruptionHelperText(
          "Select a position and click Save to set interruption point"
        );
        setSelectedMessageForInterruption(message);
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleSetInterruptionPoint = (
      message: SandboxMessage,
      event: React.MouseEvent
    ) => {
      if (!interruptionMode) return;

      const target = event.target as HTMLElement;
      const clickX = event.clientX - target.getBoundingClientRect().left;
      const text = message.message || "";
      const textWidth = target.getBoundingClientRect().width;
      const avgCharWidth = textWidth / text.length;
      const charPosition = Math.round(clickX / avgCharWidth);
      const position = Math.max(0, Math.min(charPosition, text.length));

      setSelectedMessageForInterruption({
        ...message,
        meta: {
          ...message.meta,
          tempInterruptionPosition: position as number,
        },
      });
    };

    const saveInterruptionPoint = () => {
      if (!selectedMessageForInterruption) return;

      const position = selectedMessageForInterruption.meta
        ?.tempInterruptionPosition as number | undefined;
      if (position === undefined) return;

      const messageId = String(
        selectedMessageForInterruption.meta?.reference_id ||
          selectedMessageForInterruption.id
      );

      setInterruptionPoints((prevPoints) => {
        const existingPointIndex = prevPoints.findIndex(
          (p) => p.messageId === messageId
        );

        if (existingPointIndex >= 0) {
          const newPoints = [...prevPoints];
          newPoints[existingPointIndex] = {
            messageId,
            position,
          };
          return newPoints;
        } else {
          return [
            ...prevPoints,
            {
              messageId,
              position,
            },
          ];
        }
      });

      setInterruptionMode(false);
      setInterruptionHelperText("");
      setSelectedMessageForInterruption(null);
    };

    const removeInterruptionPoint = (messageId: string) => {
      setInterruptionPoints((prevPoints) =>
        prevPoints.filter((point) => point.messageId !== messageId)
      );
    };

    const getMessagesWithInterruption = useCallback(() => {
      if (interruptionPoints.length === 0) {
        return conversation.turns;
      }

      return conversation.turns.map((turn) => ({
        ...turn,
        messages: turn.messages.map((msg) => {
          const msgId = String(msg.meta?.reference_id || msg.id);
          const interruptionPoint = interruptionPoints.find(
            (p) => p.messageId === msgId
          );

          if (interruptionPoint && interruptionPoint.position !== null) {
            return {
              ...msg,
              message:
                msg.message?.substring(0, interruptionPoint.position || 0) +
                " <INTERRUPTED>",
              meta: {
                ...msg.meta,
                interruption_point: interruptionPoint.position,
                original_message: msg.message,
              },
            };
          }
          return msg;
        }),
      }));
    }, [conversation.turns, interruptionPoints]);

    // Tour steps
    const tourSteps = [
      {
        title: "Welcome to Studio",
        content:
          "This is the Studio where you can test and interact with your agents and models. Let's take a quick tour!",
        target: "body",
      },
      {
        title: "Model Selection",
        content:
          "Choose your agent or model from the dropdown. You can select from available agents or different model providers.",
        target: ".model-input",
      },
      {
        title: "Voice Recording",
        content:
          "Click the microphone button to record your voice. The system will transcribe your speech automatically.",
        target: ".voice-button",
      },
      {
        title: "Send Messages",
        content:
          "Type your message and click Send, or press Enter to send your message to the selected model.",
        target: ".send-button",
      },
      {
        title: "Interruption Points",
        content:
          "Double-click on bot messages to set interruption points. This allows you to branch conversations from specific points.",
        target: ".bot-message",
      },
      {
        title: "Performance Metrics",
        content:
          "View timing information and performance metrics for each model response. Green indicates fast performance, red indicates slower responses.",
        target: ".timing-info",
      },
    ];

    const startTour = () => {
      setTourOpen(true);
      setTourStep(0);
      setShowTourButton(false);
    };

    const nextTourStep = () => {
      if (tourStep < tourSteps.length - 1) {
        setTourStep(tourStep + 1);
      } else {
        setTourOpen(false);
      }
    };

    const prevTourStep = () => {
      if (tourStep > 0) {
        setTourStep(tourStep - 1);
      }
    };

    const skipTour = () => {
      setTourOpen(false);
    };

    // Evaluation and fault detection functions
    const handleFaultFound = () => {
      // Could add tour functionality here if needed
      console.log("Fault found in evaluation");
    };

    const applyEvaluation = (
      messages: SandboxMessage[],
      evalResponse: EvaluateResponse
    ) => {
      let foundMessage = false;
      let hasFault = false;

      setConversation((prevConversation) => {
        const newTurns = prevConversation.turns.map((turn) => {
          const messageInTurn = turn.messages.find((msg) => {
            const matches =
              String(msg.meta?.reference_id) ===
              String(evalResponse?.reference_id);
            return matches;
          });

          if (messageInTurn) {
            foundMessage = true;
            const evaluation_results_set: EvaluateResult[] =
              evalResponse.evaluation_results || [];

            const updatedMessage = { ...messageInTurn };
            if (evalResponse.correction && updatedMessage.meta) {
              updatedMessage.meta = {
                ...updatedMessage.meta,
                correction: evalResponse.correction,
              };
            }

            for (const eval_result of evaluation_results_set) {
              if (eval_result.meta && typeof eval_result.meta === "string") {
                eval_result.meta = JSON.parse(eval_result.meta);
              }
              if (eval_result?.status === "FAULT") {
                hasFault = true;

                if (
                  updatedMessage.meta &&
                  Array.isArray(updatedMessage.meta.eval_results)
                ) {
                  updatedMessage.meta.eval_results = [
                    ...updatedMessage.meta.eval_results,
                    eval_result,
                  ];
                  updatedMessage.meta.fault_found = true;
                } else {
                  updatedMessage.meta = {
                    ...updatedMessage.meta,
                    eval_results: [eval_result],
                    fault_found: true,
                  };
                }
              }
            }

            if (!hasFault && !updatedMessage.meta?.eval_results) {
              const eval_result = evaluation_results_set.sort(
                (a, b) => (a.eval_time || 0) - (b.eval_time || 0)
              )[0];
              updatedMessage.meta = {
                ...updatedMessage.meta,
                eval_results: [eval_result],
                fault_found: false,
              };
            }

            return {
              ...turn,
              messages: turn.messages.map((msg) =>
                String(msg.meta?.reference_id) ===
                String(evalResponse?.reference_id)
                  ? updatedMessage
                  : msg
              ),
            };
          }
          return turn;
        });

        return { ...prevConversation, turns: newTurns };
      });

      const strobeColor = hasFault ? "red" : "green";
      setStrobeMessage({
        id: String(evalResponse?.reference_id),
        color: strobeColor,
      });
      setTimeout(() => setStrobeMessage({ id: null, color: "" }), 5000);

      if (hasFault) {
        setEvalResponses((prevEvalResponses: EvaluateResponse[]) => {
          const exists = prevEvalResponses.some(
            (er) => er.reference_id === evalResponse.reference_id
          );

          if (!exists) {
            return [...prevEvalResponses, evalResponse];
          }
          return prevEvalResponses;
        });
      }
    };

    // WebSocket message handler
    useEffect(() => {
      onMessageHandlerRef.current = (event: MessageEvent) => {
        if (pausedRecordingRef.current) {
          return;
        }
        try {
          const response = JSON.parse(event.data);
          if (
            response.type === "vad" &&
            response.event === "pause" &&
            response.status === "success"
          ) {
            pausedRecordingRef.current = true;
            handleSend(null);
            stopRecording();
          } else if (response.status === "success") {
            if (response.transcript) {
              setInputValue(response.transcript);
            }
          } else if (response.status === "error") {
            toast.error(response.error || "Transcription error occurred");
          }
        } catch (error) {
          toast.error("Error processing server response");
        }
      };
    }, []);

    // Update interruption mode ref
    useEffect(() => {
      interruptionModeRef.current = interruptionMode;
    }, [interruptionMode]);

    // Keyboard navigation for interruption points
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (!interruptionModeRef.current || !selectedMessageForInterruption)
          return;

        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();

          const currentPosition =
            (selectedMessageForInterruption.meta
              ?.tempInterruptionPosition as number) || 0;
          const messageText = selectedMessageForInterruption.message || "";

          let newPosition = currentPosition;
          if (event.key === "ArrowLeft") {
            newPosition = Math.max(0, currentPosition - 1);
          } else {
            newPosition = Math.min(messageText.length, currentPosition + 1);
          }

          if (newPosition !== currentPosition) {
            setSelectedMessageForInterruption({
              ...selectedMessageForInterruption,
              meta: {
                ...selectedMessageForInterruption.meta,
                tempInterruptionPosition: newPosition as number,
              },
            });
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [selectedMessageForInterruption]);

    // Cleanup effects
    useEffect(() => {
      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null;
        }
      };
    }, []);

    // Model change handler
    const handleModelChange = useCallback((value: string) => {
      console.log("Chat onChange called with:", value);
      setSelectedModel(value);
      selectedModelRef.current = value;
    }, []);

    // Helper function to get model segments for timings
    const getModelSegments = useCallback(() => {
      if (modelTimings.length === 0) return [];

      const maxTimeValue = Math.max(
        500,
        ...modelTimings.map((timing) => {
          if (timing.ttftValue !== null) return timing.ttftValue;
          if (timing.ttftTime !== null)
            return timing.ttftTime - timing.startTime;
          if (timing.inferenceTime !== null) return timing.inferenceTime;
          return currentTime - timing.startTime;
        })
      );

      const segments = modelTimings.map((timing) => {
        let duration: number;
        let isComplete: boolean;

        if (timing.ttftValue !== null) {
          duration = timing.ttftValue;
          isComplete = true;
        } else if (timing.ttftTime !== null) {
          duration = timing.ttftTime - timing.startTime;
          isComplete = true;
        } else if (timing.inferenceTime !== null) {
          duration = timing.inferenceTime;
          isComplete = true;
        } else {
          duration = currentTime - timing.startTime;
          isComplete = false;
        }

        return {
          model: timing.model,
          percent: (duration / maxTimeValue) * 100,
          color: getModelColor(timing.model),
          duration: duration,
          isComplete: isComplete,
        };
      });

      return segments;
    }, [modelTimings, currentTime]);

    // Helpers for metrics panel
    const openMetricsForMessage = useCallback((message: SandboxMessage) => {
      setMetricsMessage(message);
      setMetricsOpen(true);
    }, []);

    const closeMetrics = useCallback(() => {
      setMetricsOpen(false);
      setMetricsMessage(null);
    }, []);

    const formatMs = (val?: number | null) =>
      typeof val === "number" && isFinite(val) ? `${Math.round(val)}ms` : "—";

    return (
      <div className="flex flex-col h-full bg-background">
        {interruptionMode && (
          <div className="fixed top-0 left-0 right-0 bg-red-100 text-red-800 p-2 text-center z-50 flex justify-center items-center gap-2">
            <Scissors className="w-4 h-4" />
            {interruptionHelperText}
            {selectedMessageForInterruption?.meta?.tempInterruptionPosition !==
              undefined && (
              <span className="text-xs">Use ← → keys to adjust position</span>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={saveInterruptionPoint}>
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setInterruptionMode(false);
                setInterruptionHelperText("");
                setSelectedMessageForInterruption(null);
              }}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
        {conversation.turns.length === 0 ? (
          /* Empty State */
          <div className="flex-1 flex flex-col">
            <div className="w-full max-w-2xl mx-auto px-8 space-y-6">
              <div className="text-center space-y-2 mb-8">
                <Bot className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-2xl font-gc-demi-bold text-foreground">
                  Start a Conversation
                </h2>
                <p className="text-muted-foreground">
                  Test your models and agents
                </p>
                {showTourButton && (
                  <Button
                    variant="outline"
                    onClick={startTour}
                    className="mt-4 gap-2">
                    <HelpCircle className="w-4 h-4" />
                    Take a Tour
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                <div className="model-input">
                  <ModelInput
                    value={selectedModel}
                    onChange={handleModelChange}
                    placeholder="Choose your Agent / Model"
                    label=""
                    showEmptyOption={false}
                    modelType="all"
                    agentSettings={agentSettings}
                    onAgentParamsChange={(params) => {
                      console.log(
                        "ModelInput onAgentParamsChange called with:",
                        params
                      );
                      console.log(
                        "Current selectedModelRef:",
                        selectedModelRef.current
                      );
                      setAgentId(params.agent_id);
                      setAgentSettings(params.settings);
                    }}
                  />
                </div>

                {/* Agent Settings - shown when an agent is selected */}
                {agentId && agentSettings && (
                  <div className="flex items-center gap-6 px-1">
                    {/* Execution Mode */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mode</span>
                      <Select
                        value={agentSettings.execution_mode || "auto"}
                        onValueChange={(value) =>
                          setAgentSettings({
                            ...agentSettings,
                            execution_mode: value,
                          })
                        }>
                        <SelectTrigger className="w-[130px] h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto</SelectItem>
                          <SelectItem value="direct">Direct</SelectItem>
                          <SelectItem value="route">Route</SelectItem>
                          <SelectItem value="reasoning">Reasoning</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* High Performance Toggle */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        High Performance
                      </span>
                      <Switch
                        checked={agentSettings.high_performance || false}
                        onCheckedChange={(checked) =>
                          setAgentSettings({
                            ...agentSettings,
                            high_performance: checked,
                          })
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Image Preview Area - only for models, not agents */}
                {selectedModel && !agentId && attachedImages.length > 0 && (
                  <div className="overflow-x-auto pt-3 mb-2">
                    <div className="flex gap-2">
                      {attachedImages.map((image, index) => (
                        <div key={index} className="relative group flex-shrink-0">
                          <img
                            src={image}
                            alt={`Attached ${index + 1}`}
                            className="h-14 w-14 object-cover rounded-lg border border-border"
                          />
                          <button
                            onClick={() => removeAttachedImage(index)}
                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div
                  className="flex items-center gap-2"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <Input
                    ref={inputRef}
                    placeholder="Type a message..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full text-base py-6"
                    autoFocus
                  />
                  {/* Hidden file input */}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  {/* Image upload button - only for models, not agents */}
                  {selectedModel && !agentId && (
                    <Button
                      onClick={() => imageInputRef.current?.click()}
                      variant="outline"
                      title="Attach image"
                      className="flex-shrink-0 h-[50px] px-4">
                      <ImagePlus className="w-5 h-5" />
                    </Button>
                  )}
                </div>
                <div className="flex justify-center mt-4">
                  <Button
                    onClick={handleSendClick}
                    disabled={(!inputValue.trim() && (selectedModel && !agentId ? attachedImages.length === 0 : true)) || !selectedModel}
                    className="px-8">
                    Start Conversation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 max-w-4xl mx-auto space-y-6">
                {conversation.turns.map((turn, turnIndex) => (
                  <div key={turnIndex} className="space-y-4">
                    {turn.sender === "USER" ? (
                      /* User Messages */
                      turn.messages
                        .filter(
                          (message) =>
                            (message.message && message.message.trim() !== "") ||
                            (message.meta?.attached_images && (message.meta.attached_images as string[]).length > 0)
                        )
                        .map((message) => {
                          const attachedImgs = (message.meta?.attached_images as string[]) || [];
                          return (
                            <div
                              key={String(message.id)}
                              className="flex items-start gap-3 flex-row-reverse">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-maitai-green text-white flex items-center justify-center">
                                <User className="w-4 h-4" />
                              </div>
                              <div className="flex flex-col gap-1 items-end max-w-[80%]">
                                {/* Show attached images */}
                                {attachedImgs.length > 0 && (
                                  <div className="flex flex-wrap gap-2 justify-end mb-1">
                                    {attachedImgs.map((img, idx) => (
                                      <img
                                        key={idx}
                                        src={img}
                                        alt={`Attached ${idx + 1}`}
                                        className="max-h-48 max-w-xs rounded-lg border border-border object-contain"
                                      />
                                    ))}
                                  </div>
                                )}
                                {message.message && message.message.trim() !== "" && (
                                  <div className="rounded-lg px-4 py-2 bg-maitai-green text-white">
                                    <p className="text-sm whitespace-pre-wrap break-words">
                                      {message.message}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      /* Bot Messages */
                      <div className="space-y-3">
                        {turn.messages
                          .filter(
                            (message) =>
                              message.message && message.message.trim() !== ""
                          )
                          .map((message, messageIndex) => {
                            const modelName =
                              (message.meta?.model as string) || "";
                            const modelColor = getModelColor(modelName);
                            const ttft = message.meta?.ttft as number;
                            const infTime = message.meta?.inf_time as number;

                            return (
                              <Card
                                key={String(
                                  message.meta?.renderKey ||
                                    message.meta?.reference_id ||
                                    message.id
                                )}
                                className={`bot-message p-4 border-l-4 ${
                                  strobeMessage?.id === String(message?.id)
                                    ? `animate-pulse ${
                                        strobeMessage?.color === "red"
                                          ? "bg-red-50 border-red-200"
                                          : "bg-green-50 border-green-200"
                                      }`
                                    : ""
                                }`}
                                style={{ borderLeftColor: modelColor }}>
                                <div className="space-y-2">
                                  {/* Model Header */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Bot
                                        className="w-4 h-4"
                                        style={{ color: modelColor }}
                                      />
                                      <span
                                        className="font-gc-med text-sm"
                                        style={{ color: modelColor }}>
                                        {getModelLabel(modelName)}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      {/* Timing information */}
                                      {(ttft || infTime) && (
                                        <Tooltip
                                          content={
                                            ttft
                                              ? "Time to first token / Inference time"
                                              : "Inference time"
                                          }>
                                          <Badge
                                            variant="outline"
                                            className="timing-info gap-1">
                                            <Clock className="w-3 h-3" />
                                            {ttft && (
                                              <span
                                                style={{
                                                  color: getTimingColor(
                                                    ttft,
                                                    true
                                                  ),
                                                }}>
                                                {ttft}
                                              </span>
                                            )}
                                            {ttft && infTime && <span>/</span>}
                                            {infTime && (
                                              <span
                                                style={{
                                                  color: getTimingColor(
                                                    infTime,
                                                    false
                                                  ),
                                                }}>
                                                {infTime}
                                              </span>
                                            )}
                                            <span className="text-muted-foreground">
                                              ms
                                            </span>
                                          </Badge>
                                        </Tooltip>
                                      )}

                                      {/* Markdown/Raw toggle button */}
                                      <Tooltip
                                        content={
                                          markdownEnabledMessages.has(
                                            String(message.id)
                                          )
                                            ? "Show Raw Text"
                                            : "Show Markdown"
                                        }>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            const messageId = String(
                                              message.id
                                            );
                                            setMarkdownEnabledMessages(
                                              (prev) => {
                                                const newSet = new Set(prev);
                                                if (newSet.has(messageId)) {
                                                  newSet.delete(messageId);
                                                } else {
                                                  newSet.add(messageId);
                                                }
                                                return newSet;
                                              }
                                            );
                                          }}>
                                          {markdownEnabledMessages.has(
                                            String(message.id)
                                          ) ? (
                                            <Code className="w-3 h-3" />
                                          ) : (
                                            <FileText className="w-3 h-3" />
                                          )}
                                        </Button>
                                      </Tooltip>

                                      {/* Branch button - show for all conversations on first message */}
                                      {messageIndex === 0 && (
                                        <Tooltip content="Branch from this point to create a new conversation path">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                              handleBranchFromTurn(
                                                turnIndex,
                                                message
                                              )
                                            }>
                                            <GitBranch className="w-3 h-3" />
                                          </Button>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>

                                  {/* Message Content */}
                                  <div
                                    className={cn(
                                      "text-sm text-foreground cursor-pointer",
                                      markdownEnabledMessages.has(
                                        String(message.id)
                                      )
                                        ? "space-y-2"
                                        : "whitespace-pre-wrap break-words"
                                    )}
                                    onClick={(e) =>
                                      interruptionMode
                                        ? handleSetInterruptionPoint(message, e)
                                        : openMetricsForMessage(message)
                                    }
                                    onDoubleClick={(e) =>
                                      handleMessageDoubleClick(message, e)
                                    }>
                                    {(() => {
                                      const interruptionPoint =
                                        interruptionPoints.find(
                                          (p) =>
                                            p.messageId ===
                                            String(
                                              message.meta?.reference_id ||
                                                message.id
                                            )
                                        );

                                      const messageText = message.message || "";
                                      const isMarkdown =
                                        markdownEnabledMessages.has(
                                          String(message.id)
                                        );

                                      if (
                                        interruptionPoint &&
                                        interruptionPoint.position !== null
                                      ) {
                                        const before = messageText.substring(
                                          0,
                                          interruptionPoint.position || 0
                                        );
                                        const after = messageText.substring(
                                          interruptionPoint.position || 0
                                        );
                                        return (
                                          <>
                                            {isMarkdown ? (
                                              <div>
                                                {renderMarkdown(before)}
                                              </div>
                                            ) : (
                                              <span>{before}</span>
                                            )}
                                            <span className="inline-block w-0.5 h-4 bg-red-500 mx-1 align-middle" />
                                            <span className="line-through text-gray-500">
                                              {isMarkdown
                                                ? renderMarkdown(after)
                                                : after}
                                            </span>
                                          </>
                                        );
                                      } else if (
                                        selectedMessageForInterruption?.meta
                                          ?.reference_id ===
                                          message.meta?.reference_id &&
                                        typeof selectedMessageForInterruption
                                          ?.meta?.tempInterruptionPosition ===
                                          "number"
                                      ) {
                                        const before = messageText.substring(
                                          0,
                                          selectedMessageForInterruption.meta
                                            .tempInterruptionPosition
                                        );
                                        const after = messageText.substring(
                                          selectedMessageForInterruption.meta
                                            .tempInterruptionPosition
                                        );
                                        return (
                                          <>
                                            {isMarkdown ? (
                                              <div>
                                                {renderMarkdown(before)}
                                              </div>
                                            ) : (
                                              <span>{before}</span>
                                            )}
                                            <span className="inline-block w-0.5 h-4 bg-orange-500 mx-1 align-middle" />
                                            <span className="opacity-50">
                                              {isMarkdown
                                                ? renderMarkdown(after)
                                                : after}
                                            </span>
                                          </>
                                        );
                                      } else {
                                        return isMarkdown
                                          ? renderMarkdown(messageText)
                                          : messageText;
                                      }
                                    })()}
                                  </div>

                                  {/* Evaluation Results */}
                                  {Boolean(
                                    message.meta &&
                                      Array.isArray(
                                        (message.meta as any).eval_results
                                      ) &&
                                      (message.meta as any).fault_found
                                  ) && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {(
                                        (message.meta as any)
                                          .eval_results as EvaluateResult[]
                                      ).map(
                                        (
                                          eval_result: EvaluateResult,
                                          index: number
                                        ) =>
                                          eval_result.sentinel_name ? (
                                            <Tooltip
                                              key={index}
                                              content={`${
                                                eval_result.description
                                              }${
                                                eval_result.meta?.correction
                                                  ? `\n\nCorrection: "${eval_result.meta.correction}"`
                                                  : ""
                                              }`}>
                                              <Badge
                                                variant={
                                                  eval_result.severity === 3
                                                    ? "destructive"
                                                    : eval_result.severity === 2
                                                    ? "destructive"
                                                    : "secondary"
                                                }
                                                className="cursor-help">
                                                {eval_result.sentinel_name}
                                              </Badge>
                                            </Tooltip>
                                          ) : null
                                      )}
                                    </div>
                                  )}

                                  {/* Correction/Uncorrection Tag */}
                                  {message.sender === "BOT" &&
                                    (message.meta as any)?.eval_results &&
                                    (message.meta as any)?.fault_found && (
                                      <div className="mt-2">
                                        <Tooltip
                                          content={
                                            message.meta?.original
                                              ? `Original: "${message.meta.original}"`
                                              : "Output has faults that were not corrected."
                                          }>
                                          <Badge
                                            variant={
                                              message.meta?.original
                                                ? "default"
                                                : "destructive"
                                            }>
                                            {message.meta?.original
                                              ? "Corrected"
                                              : "Uncorrected"}
                                          </Badge>
                                        </Tooltip>
                                      </div>
                                    )}
                                </div>
                              </Card>
                            );
                          })}

                        {/* Performance Bar for this turn */}
                        {isTyping &&
                          turnIndex === conversation.turns.length - 1 &&
                          modelTimings.length > 0 && (
                            <div className="mt-2">
                              <div className="h-3 w-full bg-muted rounded-full overflow-hidden flex">
                                {getModelSegments().map((segment, idx) => (
                                  <Tooltip
                                    key={idx}
                                    content={`${segment.model
                                      .split(":")
                                      .pop()} - ${
                                      segment.isComplete
                                        ? `Time to first token: ${Math.round(
                                            segment.duration
                                          )}ms`
                                        : "Waiting for first token..."
                                    }`}>
                                    <div
                                      className="h-full transition-all duration-300"
                                      style={{
                                        width: `${segment.percent}%`,
                                        backgroundColor: segment.color,
                                        opacity: segment.isComplete ? 1 : 0.7,
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing Indicator - Simple pulse */}
                {isTyping && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse" />
                    <span>Thinking...</span>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input Container - Anchored to bottom */}
            <div className="border-t border-border p-4 bg-background flex-shrink-0">
              {/* Image Preview Area - only for models, not agents */}
              {selectedModel && !agentId && attachedImages.length > 0 && (
                <div className="max-w-4xl mx-auto overflow-x-auto pt-3 mb-2">
                  <div className="flex gap-2">
                    {attachedImages.map((image, index) => (
                      <div key={index} className="relative group flex-shrink-0">
                        <img
                          src={image}
                          alt={`Attached ${index + 1}`}
                          className="h-14 w-14 object-cover rounded-lg border border-border"
                        />
                        <button
                          onClick={() => removeAttachedImage(index)}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div
                className="max-w-4xl mx-auto flex items-center gap-2"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {/* Hidden file input */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Input
                  ref={inputRef}
                  placeholder="Type a message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1"
                  disabled={isTyping}
                  autoFocus
                />
                {/* Image upload button - only for models, not agents */}
                {selectedModel && !agentId && (
                  <Button
                    onClick={() => imageInputRef.current?.click()}
                    variant="outline"
                    disabled={isTyping}
                    title="Attach image"
                    className="h-10 px-3">
                    <ImagePlus className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  onClick={isRecording ? stopRecording : startRecording}
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  disabled={isTyping}
                  className="voice-button relative overflow-hidden">
                  {isRecording ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                  <AudioVisualization
                    levels={audioLevels}
                    isRecording={isRecording}
                  />
                </Button>
                <Button
                  onClick={handleSendClick}
                  disabled={(!inputValue.trim() && (selectedModel && !agentId ? attachedImages.length === 0 : true)) || isTyping}
                  className="send-button">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Metrics Side Panel */}
            {metricsOpen && (
              <div className="fixed top-0 right-0 h-full w-[420px] border-l border-border bg-background/95 backdrop-blur z-40 shadow-xl">
                <div className="h-full flex flex-col">
                  <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-background/95">
                    <div className="font-semibold text-lg text-muted-foreground tracking-wide">
                      Completion Details
                    </div>
                    <Button size="sm" variant="ghost" onClick={closeMetrics}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="p-6 space-y-6 text-[16px]">
                    {(() => {
                      const m = (metricsMessage?.meta as any) || {};
                      return <CompletionDetailsPanel meta={m} />;
                    })()}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tour Overlay */}
        {tourOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    {tourSteps[tourStep].title}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={skipTour}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-gray-600">{tourSteps[tourStep].content}</p>
                <div className="flex justify-between items-center">
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={prevTourStep}
                      disabled={tourStep === 0}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={nextTourStep}>
                      {tourStep === tourSteps.length - 1 ? "Finish" : "Next"}
                    </Button>
                  </div>
                  <div className="text-sm text-gray-500">
                    {tourStep + 1} of {tourSteps.length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

Chat.displayName = "Chat";

export default Chat;
